import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import {
  BadgeCheck, Briefcase, CalendarDays, FileBarChart2, HeartHandshake, Home, LayoutDashboard,
  LogOut, MessageSquare, Moon, Receipt, Settings as SettingsIcon, ShieldCheck, Sun, UserCog, Users,
} from "lucide-react";
import { currentRole, logout } from "../lib/api";
import { useTheme } from "../lib/theme";
import NotificationBell from "./NotificationBell";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/participants", label: "Participants", icon: Users },
  { to: "/roster", label: "Roster", icon: CalendarDays },
  { to: "/billing", label: "Billing", icon: Receipt },
  { to: "/team", label: "Team", icon: UserCog },
  { to: "/compliance", label: "Compliance", icon: BadgeCheck },
  { to: "/hr", label: "HR", icon: Briefcase },
  { to: "/homes", label: "Homes", icon: Home },
  { to: "/quality", label: "Quality", icon: ShieldCheck },
  { to: "/reports", label: "Reports", icon: FileBarChart2 },
  { to: "/messages", label: "Messages", icon: MessageSquare },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function Layout() {
  const navigate = useNavigate();
  const [theme, toggleTheme] = useTheme();
  if (currentRole() === "participant") return <Navigate to="/portal" replace />;
  return (
    <div className="min-h-screen flex">
      <aside className="w-60 shrink-0 border-r border-edge bg-surface flex flex-col">
        <div className="flex items-center gap-3 px-5 py-5 border-b border-edge-soft">
          <div className="h-9 w-9 rounded-xl bg-teal-700 flex items-center justify-center">
            <HeartHandshake className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-[15px] font-semibold leading-tight text-ink">NDIS Platform</p>
            <p className="text-xs text-faint leading-tight">Sunrise Care</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-teal-50 text-teal-800 font-medium"
                    : "text-mut hover:bg-hover hover:text-body"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-edge-soft space-y-0.5">
          <button
            onClick={toggleTheme}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-mut hover:bg-hover hover:text-body transition-colors"
          >
            {theme === "dark" ? <Sun className="h-[18px] w-[18px]" strokeWidth={1.8} /> : <Moon className="h-[18px] w-[18px]" strokeWidth={1.8} />}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </button>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-mut hover:bg-hover hover:text-body transition-colors"
          >
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.8} />
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="w-full px-8 pt-4">
          <div className="flex justify-end">
            <NotificationBell />
          </div>
        </div>
        <div className="w-full px-8 pb-8 pt-2">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
